import "swiper/css";
import { isDefined } from "@clipboard-health/util-ts";
import { RefresherEventDetail } from "@ionic/core";
import {
  IonAlert,
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { Box } from "@mui/material";
import { ShiftFilters } from "@src/app/components/shiftFilters";
import { useAgentPreferences } from "@src/appV2/Agents/api/useAgentPreferences";
import { useAgentProfile } from "@src/appV2/Agents/api/useAgentProfile";
import { ShiftPeriod } from "@src/appV2/Shifts/Shift/types";
import { DISTANCE_PREFERENCE_MAX_VALUE, SEARCH_MODE } from "@src/constants";
import { Shift, ShiftTypeLabels } from "@src/lib/interface";
import { groupBy } from "lodash";
import moment from "moment-timezone";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { fetchCurrentTimezone, fetchYourShiftsByDateAggregated } from "./api";
import { GroupItem } from "./groupItem";
import { ShiftDateMap, ShiftListGroupState } from "./model";
import { ShiftListPage } from "./shiftList";
import { ShiftNameLoader } from "./shiftNameLoader";
import { IBookedBy, IShiftSlot } from "../components/workWithFriends/interface";
import {
  fetchFriendsByShiftType,
  fetchShiftSlotsFromOpenShifts,
} from "../components/workWithFriends/utils";
import { fetchOpenShifts } from "../openShifts/api";
import { RequestOpenShiftOptions } from "../openShifts/model";
import { AppV2AccountRoutes } from "../routing/constant/appV2AccountRoutes";
import { useAppSelector } from "../store";
import { useDefinedAgent } from "../store/helperHooks";
import { groupByShiftName, removeSharedShiftSlot } from "../store/shift";
import { IWorkerFriend } from "../store/workerFriends/model";

const OpenShiftDayView: FC = () => {
  const { date } = useParams<{ date?: string }>() as any;
  const dispatch = useDispatch();
  const agent = useDefinedAgent();
  const sharedShiftSlot = useAppSelector((state) => state.shiftStore?.sharedShiftSlot);
  const { sharedFacilityId, sharedShiftStart, sharedShiftEnd, sharedShiftName } =
    sharedShiftSlot || {};

  const workerFriends: IWorkerFriend[] = useAppSelector(
    (state) => state.workerFriendsStore.workerFriends.data ?? []
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [noShiftAlert, setNoShiftAlert] = useState<boolean>(false);
  const [myShiftMap, setMyShiftMap] = useState<ShiftDateMap>({});
  const [openShiftMap, setOpenShiftMap] = useState<ShiftDateMap>({});
  const [shiftName, setShiftName] = useState<string | null>(null);
  const [totalShiftsCount, setTotalShiftsCount] = useState<number>(0);
  const [shiftList, setShiftList] = useState<ShiftListGroupState>({
    open: [],
    assigned: [],
  });
  const history = useHistory();
  const [openShiftSlots, setOpenShiftSlots] = useState<IShiftSlot[]>([]);
  const [friendsMap, setFriendsMap] = useState<Map<ShiftPeriod, IBookedBy[]> | null>(null);

  const { filters, setFilters } = useAgentPreferences();
  const { data: agentProfile } = useAgentProfile();

  const distancePreference = filters?.distance ?? DISTANCE_PREFERENCE_MAX_VALUE;
  const qualificationPreference = filters?.license ?? "";

  const getOpenAndMyShifts = useCallback(
    (distancePreference?: number, qualificationPreference?: string) => {
      if (
        agent?.geoLocation?.coordinates &&
        isDefined(date) &&
        isDefined(distancePreference) &&
        isDefined(qualificationPreference)
      ) {
        (async function () {
          try {
            setLoading(true);
            const options: RequestOpenShiftOptions = {
              dateFilter: { start: date, end: date },
              coordinates: agent.geoLocation?.coordinates ?? [],
              distance: distancePreference,
              qualification: qualificationPreference,
              specialities: agent.specialities ?? {},
            };
            const [openShiftResults, myShifts]: [Shift[] | undefined, void | ShiftDateMap, string] =
              await Promise.all([
                fetchOpenShifts(options),
                fetchYourShiftsByDateAggregated({ start: date, end: date }),
                /**
                 * FIXME:
                 * This fetch response is unused.
                 * It looks like it is copy and paste from here:
                 * https://github.com/ClipboardHealth/cbh-mobile-app/blob/1ed6d1370bc2ccba9d93e0160ea65a5705f5a1c7/src/app/dayView/facilityDetails/facilityOpenShifts.tsx#L133
                 * Delete fetchCurrentTimezone()
                 */
                fetchCurrentTimezone(),
              ]);
            if (Array.isArray(myShifts)) {
              return;
            }
            const myTodayShifts = myShifts[date] || [];
            setMyShiftMap(groupBy(myTodayShifts, groupByShiftName));

            const openShifts = openShiftResults?.filter((shift) => !shift.filtered) ?? [];

            let shiftSlotResponse: IShiftSlot[] = [];
            const myShiftsList = myShifts ? Object.values(myShifts).flat(1) : [];
            const openShiftsList = openShifts?.length ? openShifts : [];
            if (openShiftsList.length || myShiftsList.length) {
              const shiftList = [...openShiftsList, ...myShiftsList];
              shiftSlotResponse = await fetchShiftSlotsFromOpenShifts(shiftList);
              setOpenShiftSlots(shiftSlotResponse?.length ? shiftSlotResponse : []);
            } else {
              setOpenShiftSlots([]);
            }
            if (shiftSlotResponse?.length && workerFriends?.length) {
              const friendsByShiftTypeMap = fetchFriendsByShiftType(
                openShifts,
                shiftSlotResponse,
                workerFriends
              );
              setFriendsMap(friendsByShiftTypeMap?.size ? friendsByShiftTypeMap : null);
            } else {
              setFriendsMap(null);
            }
            const openShiftsCount = openShifts.length;
            setTotalShiftsCount(openShiftsCount);

            setOpenShiftMap(groupBy(openShifts, groupByShiftName));
            setLoading(false);
          } catch (err) {
            setLoading(false);
          }
        })();
      }
    },
    /**
     * FIXME: This list of dependencies is very large and indicates a need
     * to fundamentally refactor this code.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, setLoading, setMyShiftMap, setOpenShiftMap, agent]
  );

  useEffect(() => {
    const isValidDate = moment(date, "YYYY-MM-DD", true);
    if (!isValidDate) {
      return history.goBack();
    }
  }, [date, history]);

  // FIXME: Convert to Tanstack Query
  useEffect(() => {
    getOpenAndMyShifts(agent.preference?.distance, agent.preference?.qualification);
  }, [getOpenAndMyShifts, agent.preference?.distance, agent.preference?.qualification]);

  useEffect(() => {
    if (shiftName) {
      setShiftList({
        open: openShiftMap[shiftName] || [],
        assigned: myShiftMap[shiftName] || [],
      });
    } else {
      setShiftList({ open: [], assigned: [] });
    }
  }, [myShiftMap, openShiftMap, shiftName]);

  useEffect(() => {
    if (sharedShiftName && openShiftMap?.[sharedShiftName]?.length) {
      const shifts = openShiftMap?.[sharedShiftName];
      const sharedShift = shifts.find(
        (shift) =>
          shift.start === sharedShiftStart &&
          shift.end === sharedShiftEnd &&
          shift.facilityId?.toString() === sharedFacilityId
      );
      if (sharedShift) {
        setShiftList({
          open: [sharedShift],
          assigned: [],
        });
      } else {
        dispatch(removeSharedShiftSlot());
        setNoShiftAlert(true);
      }
      return;
    }
    if (
      sharedShiftName &&
      openShiftMap?.[sharedShiftName] &&
      !openShiftMap?.[sharedShiftName]?.length
    ) {
      dispatch(removeSharedShiftSlot());
      setNoShiftAlert(true);
      return;
    }
  }, [
    myShiftMap,
    openShiftMap,
    sharedShiftName,
    sharedFacilityId,
    sharedShiftStart,
    sharedShiftEnd,
    dispatch,
  ]);

  const closeShiftList = () => {
    setShiftName(null);
    if (sharedShiftSlot) {
      dispatch(removeSharedShiftSlot());
    }
  };

  const doRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await getOpenAndMyShifts(agent.preference?.distance, agent.preference?.qualification);
    event.detail.complete();
  };

  const updateShiftFields = (shift, fields) => {
    shiftList.open.forEach((current) => {
      if (current._id === shift._id) {
        for (const field of fields) {
          current[field] = shift[field];
        }
      }
    });

    setShiftList(shiftList);
  };

  const goToDocumentsPage = () => {
    setShiftName(null);
    history.push(AppV2AccountRoutes.DOCUMENTS);
  };

  const shiftSelectedName = sharedShiftName || shiftName;

  return (
    <Fragment>
      <IonPage>
        <IonHeader no-border>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton text="" defaultHref="/home/openShifts" mode="ios" />
            </IonButtons>
            <IonTitle>{moment(date).format("dddd, MMMM DD YYYY")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Box sx={{ paddingY: 1 }}>
            <ShiftFilters
              totalShifts={totalShiftsCount}
              distancePreference={distancePreference}
              qualificationPreference={qualificationPreference}
              searchMode={SEARCH_MODE.DATE}
              setFilters={setFilters}
              agentLicenses={agentProfile?.licensesData ?? []}
            />
          </Box>
          <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
            <IonRefresherContent />
          </IonRefresher>
          {loading ? (
            <ShiftNameLoader listLength={ShiftTypeLabels.length} />
          ) : (
            <IonList>
              {ShiftTypeLabels.map((name) => (
                <GroupItem
                  shiftName={name}
                  key={name}
                  myShiftMap={myShiftMap}
                  openShiftMap={openShiftMap}
                  onClick={setShiftName}
                  {...(friendsMap?.size ? { friendsList: friendsMap?.get(name) } : {})}
                />
              ))}
            </IonList>
          )}
        </IonContent>
        <IonModal isOpen={!!shiftSelectedName} onDidDismiss={closeShiftList}>
          {shiftSelectedName && (
            <ShiftListPage
              onClose={closeShiftList}
              goToDocumentsPage={goToDocumentsPage}
              day={date}
              shiftName={shiftSelectedName}
              shifts={shiftList}
              getOpenAndMyShifts={() => {
                getOpenAndMyShifts(agent.preference?.distance, agent.preference?.qualification);
              }}
              loadingShiftList={loading}
              searchMode={SEARCH_MODE.TIME_SLOT}
              distancePreference={distancePreference}
              qualificationPreference={qualificationPreference}
              filterDays={1}
              shiftSlots={openShiftSlots}
              updateShiftFields={updateShiftFields}
            />
          )}
        </IonModal>
        <IonAlert
          message="This shift is no longer available"
          isOpen={noShiftAlert}
          backdropDismiss={true}
          buttons={[
            {
              text: "Ok",
              handler: () => setNoShiftAlert(false),
            },
          ]}
        />
      </IonPage>
    </Fragment>
  );
};

export { OpenShiftDayView };
